import { useCallback, useEffect, useMemo, useRef, type JSX, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { ITEMS, ghosts } from '@yuan-math/content';
import { namedTricksOf, type LearnerProfile, type MasteryLevel } from '@yuan-math/learning';
import {
  bearing,
  equippedItems,
  ghostIndex,
  hauntings,
  nearestPlace,
  placeAt,
  placeKey,
  positionKey,
  regionPlace,
  terrainAt,
  type BossDef,
  type Haunting,
  type Kingdom,
  type Place,
  type PlaceMap,
  type Position,
  type RegionMap,
  type RegionProgress,
  type Town,
} from '@yuan-math/world';
import { Avatar, type AvatarEquipment } from '@yuan-math/ui';
import { registry, useLearner } from '../state/LearnerContext.tsx';
import { BANDS } from './MasteryBand.tsx';
import { KEYS, Scenery, useCamera, useWalk, VILLAGE_WORD } from './tiles.tsx';

/**
 * The maps inside a kingdom — the village you are standing in, and the inside
 * of any place on it (docs/rpg.md §22).
 *
 * The overworld shows a student that the calculus is a long way past the
 * arithmetic. It cannot show them anything about the inside of the kingdom they
 * are in, and the honest, flat answer the kingdom page gives — five region
 * cards in a row — makes five quite different places look like five identical
 * folders. So this draws them as places, and then draws the inside of *those*:
 * walk to Sum Cottage, go in, and there is a hearth, a pantry and a table with
 * one concept standing at each.
 *
 * **Nothing here is a way in that the page did not already have.** Every plate
 * on every one of these maps opens a card, a button or a link that is already
 * on the kingdom page: a region is a card six inches below, and a concept is a
 * link inside that card. The map is the picture; the cards are the index.
 * Losing the map costs a student nothing but the pleasure of it, which is
 * exactly the weight it should carry.
 *
 * **Walking is not an access control** (`docs/rpg-implementation-plan.md`
 * §0.1). A pond refuses a step and a wall refuses a step; neither refuses a
 * region or a concept, and `validateTown` and `validateRegionMap` prove it over
 * the real maps on every `npm run content:validate`. A sealed kingdom draws all
 * of this exactly as an open one does — a seal holds back a story, never a lane
 * and never a door.
 *
 * **Which map you are in is not stored, and neither is where you are standing.**
 * Both live as long as the page does. Being inside the cottage is not a fact
 * about anybody's mathematics, and a reload putting a student back on the
 * village green is the more useful answer as well as the cheaper one.
 *
 * The tiles, the scenery, the walk and the reduced-motion rule are all
 * `tiles.tsx`, shared with the overworld: three maps, one set of ground.
 */

/** How far the live region looks for a place worth mentioning. A village is small. */
const ANNOUNCE_RADIUS = 6;

/** The bestiary is a pure function of static content, so it is built once. */
const BESTIARY = ghostIndex(ghosts);

const equipmentOf = (profile: LearnerProfile | null): AvatarEquipment => {
  if (!profile) return {};
  const worn = equippedItems(ITEMS, profile);
  return { held: worn.held?.art, cape: worn.cape?.art, headgear: worn.headgear?.art };
};

/**
 * What a place is called, drawn as, and what happens when you reach it.
 *
 * Resolved from the kingdom and the curriculum rather than from the map: a
 * place carries an id and nothing else, so a plate and the card it belongs to
 * cannot drift apart. `note` is the small word under the name — never a colour
 * alone, and never a status the thing does not have. A region is never sealed
 * and neither is a concept, so neither of them ever says so; what they say is a
 * count and a mastery band, which are the words used everywhere else.
 *
 * Exactly one of `href` and `go` is set. `href` is a place on another page, or
 * elsewhere on this one; `go` is a place on this same map that is simply *in*
 * something, and going in is a change of scenery rather than a navigation.
 */
type Sign = {
  emoji: string;
  name: string;
  note: string;
  action: string;
  href?: string;
  go?: () => void;
};

type SignContext = {
  kingdom: Kingdom;
  progressOf: Map<string, RegionProgress>;
  boss: BossDef | undefined;
  bossBeaten: boolean;
  masteryOf: (id: string) => { level: MasteryLevel };
  /**
   * What is standing on a concept, if anything — the student's own recent
   * mistakes, given a shape (`hauntings` in `@yuan-math/world`).
   *
   * **Never a lock.** It changes the plate's emoji, its word and the sentence
   * on the button, and it changes nothing else: a haunted site opens exactly as
   * an unhaunted one does. A ghost is a reason to go somewhere.
   */
  hauntingOf: (conceptId: string) => Haunting | undefined;
  /** Which regions have a map of their own to walk into. */
  hasMap: (regionId: string) => boolean;
  enter: (regionId: string) => void;
  leave: () => void;
  /** Whether the map being drawn is a region's inside rather than the village. */
  indoors: boolean;
};

function signFor(place: Place, ctx: SignContext): Sign | undefined {
  switch (place.kind) {
    case 'region': {
      const region = ctx.kingdom.regions.find((candidate) => candidate.id === place.regionId);
      if (!region) return undefined;
      const progress = ctx.progressOf.get(region.id);
      const note = progress ? `${progress.known}/${progress.total} known` : `${region.conceptIds.length} skills`;
      /*
       * A region with a map of its own is walked into; one without is what
       * every region was before the first was surveyed — a card, which the
       * signpost scrolls to. Both are the same promise kept two ways.
       */
      return ctx.hasMap(region.id)
        ? { emoji: region.emoji, name: region.name, note, action: `Go into ${region.name}`, go: () => ctx.enter(region.id) }
        : { emoji: region.emoji, name: region.name, note, action: `Go to ${region.name}`, href: `#${region.id}` };
    }
    case 'concept': {
      const concept = registry.concept(place.conceptId);
      if (!concept) return undefined;
      /*
       * A concept place is a *warded site* (docs/rpg.md §16), so walking onto
       * it opens the encounter rather than the concept page. Nothing is lost by
       * that and it is worth being precise about why: the encounter opens on
       * its Ward — the concept's own lesson — with the concept page, the whole
       * lesson and plain practice all one tap away from it. The student lands
       * one step from everything the old signpost went to, and one step from
       * the mathematics as well.
       *
       * A site with no exercises would be a door onto nothing, so a `planned`
       * concept keeps the old behaviour and goes to its page.
       */
      const haunting = ctx.hauntingOf(concept.id);
      const haunted = haunting !== undefined && !haunting.sealed;
      if (concept.status !== 'available') {
        return {
          emoji: '📗',
          name: concept.title,
          note: 'On the horizon',
          action: `Open ${concept.title}`,
          href: `/concepts/${concept.id}`,
        };
      }
      return {
        // The ghost is the emoji *and* the word, never a colour and never the
        // emoji alone (`CLAUDE.md`): the plate says "haunted" underneath it.
        emoji: haunted ? (haunting?.ghost.emoji ?? '👻') : '📗',
        name: concept.title,
        note: haunted ? 'Haunted' : BANDS[ctx.masteryOf(concept.id).level].label,
        action: haunted ? `Face what haunts ${concept.title}` : `Face the site at ${concept.title}`,
        href: `/adventure/encounter/${concept.id}`,
      };
    }
    case 'boss': {
      if (!ctx.boss) return undefined;
      return {
        emoji: ctx.boss.emoji,
        name: ctx.boss.name,
        note: ctx.bossBeaten ? '👑 Beaten' : '⚔️ Boss',
        action: ctx.bossBeaten ? `Fight ${ctx.boss.name} again` : `Challenge ${ctx.boss.name}`,
        href: `/adventure/boss/${ctx.boss.id}`,
      };
    }
    case 'quests':
      return {
        emoji: place.emoji,
        name: place.name,
        note: '📜 Quests',
        action: 'Read the quest board',
        href: '#quest-board',
      };
    case 'road':
      /*
       * The way out, one scale up. Which scale that is depends on the map it
       * stands on and never on the place, because there is only ever one answer
       * and a field for it would be a field that could be wrong.
       */
      return ctx.indoors
        ? { emoji: place.emoji, name: place.name, note: '🏡 Village', action: `Back to ${ctx.kingdom.name}`, go: ctx.leave }
        : { emoji: place.emoji, name: place.name, note: '🗺️ Leave', action: 'Back to the world map', href: '/adventure/map' };
    default:
      return undefined;
  }
}

/* -------------------------------------------------------------------------- */
/* One map                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * The canvas, the walk and the panel under it.
 *
 * Rendered with the map's own key, so moving between maps genuinely mounts a
 * new one and puts the student down where `arriveAt` says. Carrying a position
 * from one map to the next would land them in a wall — `useWalk` holding a
 * position that outlived the ground under it is the bug that would only show up
 * on the third or fourth map somebody drew.
 */
function MapCanvas({
  map,
  arriveAt,
  label,
  signs,
  profile,
}: {
  map: PlaceMap;
  /**
   * Where to put the student down — usually the map's own arrival tile, but
   * the gate they just came out of when they are coming back.
   */
  arriveAt: Position;
  label: string;
  signs: Map<string, Sign>;
  profile: LearnerProfile | null;
}): JSX.Element {
  /*
   * A tapped route walks round the other signposts on the way: arriving
   * somewhere takes you there, so a route drawn over a gate would carry a
   * student into a kingdom they meant to walk past.
   */
  const detour = useCallback((tile: Position) => placeAt(map, tile) !== undefined, [map]);
  const { position, walkTo, move } = useWalk(map, arriveAt, detour);
  const camera = useCamera(map, position);

  /*
   * The ground, built once.
   *
   * Nothing about a tile depends on where the student is standing, so nothing
   * about a tile is redrawn when they move: the camera is one transform on the
   * canvas and the walker is one more.
   */
  const tiles = useMemo(
    () =>
      map.rows.map((row, y) =>
        [...row].map((_char, x) => {
          const tile = { x, y };
          const kind = terrainAt(map, tile) ?? 'water';
          const place = placeAt(map, tile);
          const sign = place ? signs.get(placeKey(place)) : undefined;

          return (
            <div
              key={`${x},${y}`}
              className={`ow-tile ow-tile--${kind}`}
              /* Decorative: the live region below says where you are, and four
                 hundred tiles in the accessibility tree would say nothing. */
              aria-hidden="true"
              onClick={() => walkTo(tile)}
            >
              <Scenery terrain={kind} position={tile} />
              {place && sign ? (
                <span className={`ow-gate ow-place ow-place--${place.kind}`}>
                  <span className="ow-gate__mark">{sign.emoji}</span>
                  <span className="ow-gate__name">
                    {sign.name}
                    <span className="ow-gate__status">{sign.note}</span>
                  </span>
                </span>
              ) : null}
            </div>
          );
        }),
      ),
    [map, signs, walkTo],
  );

  const standingOn = placeAt(map, position);
  const sign = standingOn ? signs.get(placeKey(standingOn)) : undefined;
  const goRef = useRef<HTMLAnchorElement & HTMLButtonElement>(null);


  /*
   * Arriving somewhere *is* going there (docs/rpg.md §22).
   *
   * Walking to the cottage and then reaching for a button below the map is a
   * form, not a game: every tile map ever made puts you through the door when
   * you step on it. So an arrival clicks the panel's own control, which is the
   * same thing Enter does and keeps one definition of what each signpost means.
   *
   * The guard is the tile the student was *put down* on, not a "first render"
   * flag. Leaving a region lands them on that region's gate, and a flag would
   * walk them straight back inside it — and on the continent, opening the page
   * at the gate of the kingdom they are working in would navigate before they
   * had touched anything. Keyed by tile, it is also safe to run twice, which
   * React's strict mode does.
   */
  const handled = useRef(positionKey(arriveAt));
  useEffect(() => {
    const key = positionKey(position);
    if (handled.current === key) return;
    handled.current = key;
    goRef.current?.click();
  }, [position]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const direction = KEYS[event.key];
    if (direction) {
      event.preventDefault();
      move(direction);
      return;
    }
    if ((event.key === 'Enter' || event.key === ' ') && goRef.current) {
      event.preventDefault();
      goRef.current.click();
    }
  };

  /* ------------------------------------------------------------ the words --- */

  const terrain = terrainAt(map, position) ?? 'grass';
  const near =
    nearestPlace(map, position, ANNOUNCE_RADIUS, (place) => place.kind !== 'road') ??
    nearestPlace(map, position, ANNOUNCE_RADIUS);
  const nearSign = near ? signs.get(placeKey(near.place)) : undefined;

  const sentence = sign
    ? `${sign.name} — ${sign.note}. Press Enter to go there.`
    : nearSign && near
      ? `${VILLAGE_WORD[terrain]}. ${nearSign.name} is ${near.steps} ${near.steps === 1 ? 'step' : 'steps'} ${bearing(position, near.place)}.`
      : `${VILLAGE_WORD[terrain]}. Nothing within ${ANNOUNCE_RADIUS} steps.`;

  /*
   * A signpost either points somewhere — another page, or a card on this one —
   * or it points *inside*, which is not a navigation at all. The first is an
   * anchor and the browser has always done it better than a router can; the
   * second is a button, because a button is what a thing that changes what is
   * on the screen has to be.
   */
  const go =
    sign === undefined ? null : sign.href !== undefined ? (
      sign.href.startsWith('#') ? (
        <a ref={goRef} href={sign.href} className="btn btn--primary btn--sm overworld__enter">
          <span aria-hidden="true">{sign.emoji}</span> {sign.action}
        </a>
      ) : (
        <Link ref={goRef} to={sign.href} className="btn btn--primary btn--sm overworld__enter">
          <span aria-hidden="true">{sign.emoji}</span> {sign.action}
        </Link>
      )
    ) : (
      <button ref={goRef} type="button" onClick={sign.go} className="btn btn--primary btn--sm overworld__enter">
        <span aria-hidden="true">{sign.emoji}</span> {sign.action}
      </button>
    );

  return (
    /* The map's size is a CSS variable rather than a layout: a cottage is
       seventeen tiles across and a village twenty-six, and a frame that stayed
       the width of the page around the smaller one would be a lot of grass and
       a map in the corner of it. */
    <div
      className="overworld overworld--town"
      style={{ ['--ow-width' as string]: map.width, ['--ow-height' as string]: map.height }}
    >
      {/*
        `role="application"` because the arrow keys are the content, and
        defensible for the same reason it is on the continent: it is never the
        only route. Every place on this map is a card or a button on this same
        page, in the accessibility tree as an ordinary link.
      */}
      <div
        className="overworld__viewport"
        role="application"
        aria-roledescription="Walkable map"
        aria-label={`${label}. Arrow keys or W A S D to walk, Enter to go where you are standing.`}
        aria-describedby="town-here"
        tabIndex={0}
        onKeyDown={onKeyDown}
        ref={camera.frameRef}
      >
        <div className="overworld__canvas" style={{ transform: camera.transform }} ref={camera.canvasRef}>
          {tiles}

          <div
            className="overworld__walker"
            style={{
              transform: `translate3d(calc(${position.x} * var(--ow-tile)), calc(${position.y} * var(--ow-tile)), 0)`,
            }}
          >
            <Avatar
              config={profile?.rpg?.character?.avatar}
              classId={profile?.rpg?.character?.classId}
              equipment={equipmentOf(profile)}
              size={30}
            />
          </div>
        </div>
      </div>

      <div className="overworld__bar">
        <p className="overworld__here" id="town-here" role="status">
          {sentence}
        </p>
        {go}
      </div>

      <p className="overworld__help">
        <span aria-hidden="true">⌨️</span> Arrow keys or <kbd>W</kbd> <kbd>A</kbd> <kbd>S</kbd> <kbd>D</kbd> to walk ·
        tap a tile to walk there · walk onto a signpost to go where it points, or press <kbd>Enter</kbd> while you are
        standing on one. The water and the walls turn you back and nothing else does — everything on this map is listed
        below as well, and open to you now.
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The map you are in                                                         */
/* -------------------------------------------------------------------------- */

export function TownMap({
  town,
  inside,
  cameFrom,
  kingdom,
  progressOf,
  boss,
  bossBeaten,
  hasMap,
  onEnter,
  onLeave,
}: {
  town: Town;
  /** The region's own map, when the student has walked into one. */
  inside: RegionMap | undefined;
  /** The region they were last inside, so coming out lands them at its gate. */
  cameFrom: string | undefined;
  kingdom: Kingdom;
  progressOf: Map<string, RegionProgress>;
  boss: BossDef | undefined;
  bossBeaten: boolean;
  hasMap: (regionId: string) => boolean;
  onEnter: (regionId: string) => void;
  onLeave: () => void;
}): JSX.Element {
  const { profile, masteryOf } = useLearner();
  const map: PlaceMap = inside ?? town;

  /*
   * Where the student's ghosts are standing, derived from their own attempt
   * log. Computed here rather than passed in, for the reason `masteryOf` is:
   * both are facts about the current profile that every scale of map wants,
   * and threading them down would make the caller responsible for keeping two
   * screens in step about one student.
   */
  const haunted = useMemo(
    () => (profile ? hauntings(BESTIARY, profile.attempts, namedTricksOf(profile)) : new Map<string, Haunting>()),
    [profile],
  );

  const signs = useMemo(() => {
    const ctx: SignContext = {
      kingdom,
      progressOf,
      boss,
      bossBeaten,
      masteryOf,
      hauntingOf: (conceptId) => haunted.get(conceptId),
      hasMap,
      enter: onEnter,
      leave: onLeave,
      indoors: inside !== undefined,
    };
    const built = new Map<string, Sign>();
    for (const place of map.places) {
      const sign = signFor(place, ctx);
      if (sign) built.set(placeKey(place), sign);
    }
    return built;
  }, [boss, bossBeaten, hasMap, haunted, inside, kingdom, map.places, masteryOf, onEnter, onLeave, progressOf]);

  /*
   * Where the student lands.
   *
   * Going *in* is the map's own arrival tile. Coming back *out* is the gate
   * they came out of, not the middle of the village — walking to the far end of
   * a lane, going into the barn and being returned to the crossroads is the
   * game quietly undoing the walk, and it is worse the further out the region
   * is. `cameFrom` is only set by leaving, so entering from a card and entering
   * from a signpost still both start you at the door.
   */
  const doorway = cameFrom ? regionPlace(town, cameFrom) : undefined;
  const arriveAt: Position = inside
    ? inside.start
    : doorway
      ? { x: doorway.x, y: doorway.y }
      : town.start;

  return (
    <MapCanvas
      key={inside?.regionId ?? town.kingdomId}
      map={map}
      arriveAt={arriveAt}
      label={inside ? `${kingdom.regions.find((r) => r.id === inside.regionId)?.name ?? kingdom.name}` : kingdom.name}
      signs={signs}
      profile={profile}
    />
  );
}
